import React, { useMemo, useState } from "react";
import { Box } from "@mui/material";
// @ts-ignore
import { StriimButton } from "@striim/striim-ui";
// @ts-ignore
import { StriimMessageBox, StriimTypography } from "@striim/striim-ui-v2";
import {
    SearchFilter,
    CategoryList
} from "src/modules/apps/pages/create-app-page-templates/Components/adapter-selection/adapters-filter/create-app-page-adapters-filters";
import CreateAppPageAdaptersList from "src/modules/apps/pages/create-app-page-templates/Components/adapter-selection/adapters-list/create-app-page-adapters-list";
import {
    createAppUtils,
    CreateAppPageAdapterData
} from "src/modules/apps/pages/create-app-page-templates/create-app-page-utils";
import { styles } from "./adapter-picker-tool.styles";
import { buildCancelledResponse } from "../utils/hitl-cancel";

type Filters = { id: string; category: string };

export interface AdapterPickerToolArgs {
    type: "source" | "target";
    sourceName?: string;
}

export interface AdapterPickerToolProps {
    args: AdapterPickerToolArgs;
    respond?: (result: string) => void;
}

export const AdapterPickerTool: React.FC<AdapterPickerToolProps> = ({ args, respond }) => {
    const [selected, setSelected] = useState<CreateAppPageAdapterData | null>(null);
    const [filters, setFilters] = useState<Filters>({ id: "", category: "all" });

    const isTarget = args.type === "target";
    const type = args.type;

    const adapters = useMemo(() => {
        if (isTarget) {
            return createAppUtils.getTargets(args.sourceName);
        }
        return createAppUtils.getSources();
    }, [isTarget, args.sourceName]);

    const filteredAdapters = useMemo(() => {
        return adapters.filter(adapter => {
            const matchesSearch = adapter.id.toLowerCase().includes(filters.id.toLowerCase());
            const matchesCategory =
                filters.category === "all"
                    ? true
                    : filters.category === "new"
                    ? adapter.newlyReleased
                    : adapter.category?.toLowerCase() === filters.category?.toLowerCase();
            return matchesSearch && matchesCategory;
        });
    }, [adapters, filters]);

    const handleMissingSource = () => {
        respond?.(
            JSON.stringify({
                status: "missing-source",
                message: "A source must be selected before targets can be shown. Ask the user to pick a source first."
            })
        );
    };

    const handleSelection = (adapter: CreateAppPageAdapterData) => {
        if (selected) return;
        setSelected(adapter);
        respond?.(
            JSON.stringify({
                status: "selected",
                type: args.type,
                id: adapter.id,
                value: adapter.value,
                adapter: adapter.adapter,
                category: adapter.category
            })
        );
    };

    const handleCancel = () => {
        if (selected) return;
        respond?.(buildCancelledResponse());
    };

    // ── Guard: target requested without a source ────────────────────────────
    if (isTarget && !args.sourceName) {
        return (
            <Box sx={styles.card}>
                <Box mb={2}>
                    <StriimTypography variant="h2" sx={styles.title}>
                        Select a Target
                    </StriimTypography>
                </Box>
                <Box mb={2}>
                    <StriimMessageBox
                        type="ERROR"
                        text="A source must be selected before targets can be shown."
                        customCardStyles={{ width: "100%" }}
                    />
                </Box>
                <Box sx={styles.footer}>
                    <StriimButton variant="secondary" size="small" onClick={handleMissingSource}>
                        OK
                    </StriimButton>
                </Box>
            </Box>
        );
    }

    if (selected) {
        return (
            <Box sx={styles.card}>
                <Box sx={styles.header}>
                    <StriimTypography variant="h2" sx={styles.title}>
                        Select a {isTarget ? "Target" : "Source"}
                    </StriimTypography>
                </Box>
                <StriimMessageBox
                    type="SUCCESS"
                    text={`${isTarget ? "Target" : "Source"} selected — ${selected.id}.`}
                    customCardStyles={{ width: "100%" }}
                />
            </Box>
        );
    }

    return (
        <Box sx={styles.card}>
            <Box sx={styles.header}>
                <StriimTypography variant="h2" sx={styles.title}>
                    Select a {isTarget ? "Target" : "Source"}
                </StriimTypography>
                <StriimButton variant="secondary" size="small" onClick={handleCancel}>
                    Cancel
                </StriimButton>
            </Box>
            <SearchFilter setFilters={setFilters} type={type} />
            <CategoryList type={type} filters={filters} setFilters={setFilters} />
            <Box sx={styles.listContainer}>
                <CreateAppPageAdaptersList
                    adapters={filteredAdapters}
                    handleSelection={handleSelection}
                    type={type.toUpperCase()}
                    selectedView="list"
                    isAscendingSort
                    filters={filters}
                    hideRecentlyUsed
                />
            </Box>
        </Box>
    );
};
